import type { AuditEntry } from '@contract';
import { createColumnHelper } from '@tanstack/react-table';
import { AuditOutcome, AuditSubjectLink } from '@/components/audit/audit-sheet';
import { DataTable } from '@/components/data-table';
import { useOpenRecord } from '@/components/sheet/use-sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import type { LoadMore } from '@/components/ui/table-pager';
import { actionLabel, auditSubject } from '@/lib/audit-text';
import { formatDateTime, relativeTime } from '@/lib/format';

/** What an audit line needs; the customer page's trail has no customer or staff id. */
export type AuditLine = Pick<
  AuditEntry,
  'id' | 'at' | 'staff_name' | 'action' | 'outcome' | 'reason' | 'detail'
> &
  Partial<Pick<AuditEntry, 'customer_id'>>;

const column = createColumnHelper<AuditLine>();
const columns = [
  column.accessor('at', {
    header: 'When',
    cell: (info) => (
      <span
        className="whitespace-nowrap"
        title={formatDateTime(info.getValue())}
      >
        {relativeTime(info.getValue())}
      </span>
    ),
  }),
  column.accessor('staff_name', {
    header: 'Staff',
    cell: (info) => {
      const name = info.getValue();
      return (
        <span className="flex items-center gap-2 whitespace-nowrap">
          <Avatar size="sm">
            <AvatarFallback>
              {(name ?? 'System').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {name ?? <span className="text-muted-foreground">System</span>}
        </span>
      );
    },
  }),
  column.accessor((row) => actionLabel(row.action), {
    id: 'action',
    header: 'What happened',
    cell: (info) => (
      <span className="block font-medium">{info.getValue()}</span>
    ),
  }),
  column.display({
    id: 'subject',
    header: 'About',
    enableSorting: false,
    cell: ({ row }) => {
      const subject = auditSubject(row.original);
      return subject ? (
        <AuditSubjectLink subject={subject} lite />
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  }),
  column.accessor('reason', {
    header: 'Reason',
    cell: (info) => (
      <span className="line-clamp-2 max-w-xs">{info.getValue() ?? ''}</span>
    ),
  }),
  column.accessor('outcome', {
    header: 'Outcome',
    cell: (info) => <AuditOutcome outcome={info.getValue()} size="sm" />,
  }),
];

/** The audit log's table: plain-English actions, a link to what each is about, one pager. */
export function AuditTable({
  rows,
  empty,
  more,
}: Readonly<{
  rows: AuditLine[] | undefined;
  empty: string;
  /** A keyset list with more rows on the server: Next loads them. */
  more?: LoadMore;
}>) {
  const openRecord = useOpenRecord();
  return (
    <DataTable
      id="audit"
      columns={columns}
      data={rows}
      empty={empty}
      more={more}
      rowLabel={(row) => `Open audit entry ${row.id}`}
      onRowClick={(row, rowEl) =>
        openRecord({ type: 'audit', id: String(row.id) }, rowEl)
      }
    />
  );
}
